// INTELIMOVE-EXT-017 — Estimativas auditáveis de TAM, SAM e SOM.
// Todo número de resultado nasce de PREMISSAS registradas (valor conservador, provável e superior; unidade; moeda; período;
// fonte com tipo, data e link; confiança; justificativa). O motor é PURO e mora aqui para que a API (versão gravada) e a
// tela (prévia ao editar uma premissa, sem recarregar a página) calculem exatamente a mesma coisa.
// Regras que impedem confusões comuns:
//   • quantidade de empresas ≠ faturamento: contagem e valor são cadeias separadas; valor só existe com premissa de valor;
//   • faturamento setorial ≠ mercado: faturamento do setor só vira mercado com a parcela gasta com a categoria;
//   • população/estabelecimentos ≠ compradores: contagem que não é de empresas exige a taxa de conversão em compradoras;
//   • estimativa da IA ≠ dado oficial: o tipo da fonte é obrigatório, aparece em cada número e rebaixa a confiança.
// Sem premissa suficiente o nível fica "dados insuficientes" com o que falta — nunca um número inventado.
import { z } from 'zod';

export const SIZING_ENGINE_VERSION = 'sizing/1' as const;

// ── Vocabulário ─────────────────────────────────────────────────────────────────────────────────────────────────────
export const SIZING_METHODS = ['TOP_DOWN', 'BOTTOM_UP', 'COMBINED'] as const;
export type SizingMethod = (typeof SIZING_METHODS)[number];
export const SIZING_METHOD_LABEL: Record<SizingMethod, string> = { TOP_DOWN: 'Top-down', BOTTOM_UP: 'Bottom-up', COMBINED: 'Combinação (top-down + bottom-up)' };
export const SIZING_METHOD_HINT: Record<SizingMethod, string> = {
  TOP_DOWN: 'Parte do total do mercado (universo de empresas ou gasto do mercado com a categoria) e aplica filtros de compatibilidade e a parcela alcançável.',
  BOTTOM_UP: 'Parte de contagens de contas qualificadas e da capacidade comercial (contas trabalhadas × conversão) multiplicadas pelo valor por cliente.',
  COMBINED: 'Calcula pelos dois caminhos e mostra a faixa que cobre ambos; a diferença entre eles aparece como alerta.',
};

export const SIZING_CURRENCIES = ['BRL', 'USD', 'EUR'] as const;
export type SizingCurrency = (typeof SIZING_CURRENCIES)[number];

export const SIZING_LEVELS = ['TAM', 'SAM', 'SOM'] as const;
export type SizingLevel = (typeof SIZING_LEVELS)[number];
export const SIZING_LEVEL_LABEL: Record<SizingLevel, string> = { TAM: 'TAM — mercado total potencial', SAM: 'SAM — mercado compatível', SOM: 'SOM — parcela alcançável' };
export const SIZING_LEVEL_HINT: Record<SizingLevel, string> = {
  TAM: 'Todas as empresas (ou todo o gasto) que poderiam comprar este tipo de solução, sem filtros da sua operação.',
  SAM: 'A parte do TAM compatível com as suas ofertas, região, perfil (porte, ICP) e restrições.',
  SOM: 'A parcela do SAM que é realista conquistar no período, dentro das premissas explícitas.',
};

export const SOURCE_KINDS = ['OFICIAL', 'PUBLICA', 'OBSERVADO', 'INTERNA', 'EQUIPE', 'IA'] as const;
export type SizingSourceKind = (typeof SOURCE_KINDS)[number];
export const SOURCE_KIND_LABEL: Record<SizingSourceKind, string> = {
  OFICIAL: 'Dado oficial', PUBLICA: 'Fonte pública', OBSERVADO: 'Observado no InteliMove', INTERNA: 'Dado interno da empresa', EQUIPE: 'Premissa da equipe', IA: 'Estimativa de IA',
};
export const SOURCE_KIND_HINT: Record<SizingSourceKind, string> = {
  OFICIAL: 'Estatística de órgão oficial (ex.: IBGE), com tabela, período e link.',
  PUBLICA: 'Estudo, relatório ou publicação de terceiro com link e data.',
  OBSERVADO: 'Contado nos registros do InteliMove (empresas-alvo, segmentação). É amostra, não o total do mercado.',
  INTERNA: 'Número do seu negócio (ticket médio, conversão, capacidade comercial).',
  EQUIPE: 'Hipótese da equipe, com justificativa. Trate como premissa a validar.',
  IA: 'Número sugerido por IA. Nunca é dado oficial; rebaixa a confiança do resultado.',
};

export const CONFIDENCES = ['ALTA', 'MEDIA', 'BAIXA'] as const;
export type SizingConfidence = (typeof CONFIDENCES)[number];
export const CONFIDENCE_LABEL: Record<SizingConfidence, string> = { ALTA: 'Confiança alta', MEDIA: 'Confiança média', BAIXA: 'Confiança baixa' };

/** Unidades de contagem: só EMPRESAS entra direto; as demais exigem a conversão em empresas compradoras. */
export const COUNT_UNITS = ['EMPRESAS', 'ESTABELECIMENTOS', 'PESSOAS'] as const;
export type CountUnit = (typeof COUNT_UNITS)[number];
export const COUNT_UNIT_LABEL: Record<CountUnit, string> = { EMPRESAS: 'empresas', ESTABELECIMENTOS: 'estabelecimentos (unidades locais)', PESSOAS: 'pessoas' };

export const FILTER_DIMENSIONS = ['REGIAO', 'PORTE', 'ATIVIDADE', 'PERFIL', 'ADERENCIA', 'RESTRICAO', 'CANAL', 'OUTRO'] as const;
export type FilterDimension = (typeof FILTER_DIMENSIONS)[number];
export const FILTER_DIMENSION_LABEL: Record<FilterDimension, string> = {
  REGIAO: 'Região', PORTE: 'Porte', ATIVIDADE: 'Atividade econômica', PERFIL: 'Perfil (natureza, maturidade)', ADERENCIA: 'Aderência às ofertas/ICP',
  RESTRICAO: 'Restrição comercial', CANAL: 'Canal / acesso', OUTRO: 'Outro',
};

export const ASSUMPTION_KINDS = ['COUNT', 'RATE', 'MONEY', 'FX'] as const;
export type AssumptionKind = (typeof ASSUMPTION_KINDS)[number];

/** Papéis de premissa: cada um tem um tipo (contagem, taxa, valor, câmbio) e um lugar na fórmula. */
export const ASSUMPTION_ROLES = {
  UNIVERSE: { kind: 'COUNT', label: 'Universo de empresas (TAM)', level: 'TAM', hint: 'Quantas empresas existem nas atividades do escopo, no país. Base do TAM em empresas.' },
  BUYER_CONVERSION: { kind: 'RATE', label: 'Conversão em empresas compradoras', level: 'TAM', hint: 'Obrigatória quando o universo não está em empresas (estabelecimentos, pessoas): quantas viram empresas compradoras.' },
  MARKET_SPEND: { kind: 'MONEY', label: 'Gasto total do mercado com a categoria', level: 'TAM', hint: 'Quanto o mercado gasta por ano com este tipo de solução (top-down em valor).' },
  SECTOR_REVENUE: { kind: 'MONEY', label: 'Faturamento do setor', level: 'TAM', hint: 'Faturamento setorial NÃO é mercado: só entra multiplicado pela parcela gasta com a categoria.' },
  SPEND_SHARE: { kind: 'RATE', label: 'Parcela do faturamento gasta com a categoria', level: 'TAM', hint: 'Que fração do faturamento do setor vai para este tipo de solução.' },
  SAM_FILTER: { kind: 'RATE', label: 'Filtro de compatibilidade (SAM)', level: 'SAM', hint: 'Fração do TAM compatível em um aspecto (região, porte, aderência, restrição). Os filtros se multiplicam.' },
  QUALIFIED_ACCOUNTS: { kind: 'COUNT', label: 'Contas qualificadas contadas (SAM bottom-up)', level: 'SAM', hint: 'Bottom-up: contagem direta de empresas compatíveis (lista, cadastro). Substitui os filtros no caminho bottom-up.' },
  OBSERVED_ACCOUNTS: { kind: 'COUNT', label: 'Empresas já identificadas (piso observado)', level: 'SAM', hint: 'Empresas-alvo reais já encontradas. Não entra na fórmula: serve de piso de conferência do SAM.' },
  SOM_SHARE: { kind: 'RATE', label: 'Parcela alcançável no período (SOM)', level: 'SOM', hint: 'Top-down: fração do SAM que é realista conquistar no período.' },
  SALES_CAPACITY: { kind: 'COUNT', label: 'Contas trabalhadas no período', level: 'SOM', hint: 'Bottom-up: quantas contas a operação comercial consegue abordar no período (ex.: vendedores × contas por vendedor).' },
  WIN_RATE: { kind: 'RATE', label: 'Taxa de conversão', level: 'SOM', hint: 'Bottom-up: fração das contas trabalhadas que vira cliente.' },
  ANNUAL_VALUE: { kind: 'MONEY', label: 'Valor por cliente (ticket)', level: 'VALOR', hint: 'Quanto um cliente gasta com a sua categoria. Sem esta premissa (ou um gasto de mercado), a receita não é estimada.' },
  FX_RATE: { kind: 'FX', label: 'Taxa de câmbio', level: 'VALOR', hint: 'Converte um valor informado em outra moeda para a moeda da análise (unidades da moeda da análise por 1 da outra).' },
} as const satisfies Record<string, { kind: AssumptionKind; label: string; level: SizingLevel | 'VALOR'; hint: string }>;
export type AssumptionRole = keyof typeof ASSUMPTION_ROLES;
export const ASSUMPTION_ROLE_KEYS = Object.keys(ASSUMPTION_ROLES) as [AssumptionRole, ...AssumptionRole[]];
/** Papéis que aceitam várias premissas (os demais: uma ativa por papel; câmbio: uma por moeda de origem). */
export const MULTI_ROLES: ReadonlySet<AssumptionRole> = new Set(['SAM_FILTER', 'FX_RATE']);

/** O que cada metodologia precisa para cada nível (texto para a tela "o que falta"). */
export const METHOD_REQUIREMENTS: Record<'TOP_DOWN' | 'BOTTOM_UP', Record<SizingLevel, AssumptionRole[][]>> = {
  TOP_DOWN: { TAM: [['UNIVERSE'], ['MARKET_SPEND'], ['SECTOR_REVENUE', 'SPEND_SHARE']], SAM: [['SAM_FILTER']], SOM: [['SOM_SHARE']] },
  BOTTOM_UP: { TAM: [['UNIVERSE']], SAM: [['QUALIFIED_ACCOUNTS'], ['SAM_FILTER']], SOM: [['SALES_CAPACITY', 'WIN_RATE']] },
};

// ── Premissas ───────────────────────────────────────────────────────────────────────────────────────────────────────
const txt = (max: number) => z.string().trim().max(max);
const ymd = z.string().trim().regex(/^\d{4}(-\d{2}(-\d{2})?)?$/, 'Data no formato AAAA, AAAA-MM ou AAAA-MM-DD');
export const SizingSource = z.object({
  kind: z.enum(SOURCE_KINDS),
  name: txt(200).min(2, 'Informe a fonte (nome do órgão, estudo, sistema ou "premissa da equipe").'),
  publisher: txt(160).nullable().optional(),
  url: z.string().trim().url().max(2000).refine((u) => /^https?:\/\//i.test(u), 'Use um link http(s).').nullable().optional(),
  /** Tabela, consulta, página ou documento exato de onde o número saiu. */
  reference: txt(400).nullable().optional(),
  /** Data de referência do dado (ano do levantamento, mês da publicação). */
  referenceDate: ymd.nullable().optional(),
  /** Quando o dado foi consultado. */
  accessedAt: ymd.nullable().optional(),
}).strict();
export type SizingSource = z.infer<typeof SizingSource>;

/** Metadados de um dado oficial buscado pelo InteliMove (preenchidos pelo servidor; a tela não edita). */
export const OfficialRef = z.object({
  dataset: txt(80), table: txt(20), variable: txt(120), period: txt(10), locality: txt(200), classification: txt(600),
  /** Faixas de porte escolhidas (filtro de porte calculado a partir do dado oficial). */
  bands: z.array(txt(20)).max(12).optional(),
  /** Valores exatos devolvidos pela fonte — editar a premissa transforma a fonte em "premissa da equipe". */
  values: z.object({ low: z.number(), likely: z.number(), high: z.number() }).strict().optional(),
}).strict();
export type OfficialRef = z.infer<typeof OfficialRef>;

export const SizingAssumption = z.object({
  id: z.string().trim().regex(/^[a-z0-9_-]{2,40}$/i, 'Identificador inválido'),
  role: z.enum(ASSUMPTION_ROLE_KEYS),
  label: txt(140).min(2),
  dimension: z.enum(FILTER_DIMENSIONS).nullable().optional(),
  unit: z.enum(COUNT_UNITS).nullable().optional(),
  currency: z.enum(SIZING_CURRENCIES).nullable().optional(),
  /** Moeda de origem do câmbio (FX_RATE). */
  fromCurrency: z.enum(SIZING_CURRENCIES).nullable().optional(),
  /** Periodicidade de valores monetários. */
  per: z.enum(['ANO', 'MES']).nullable().optional(),
  low: z.number().finite().min(0),
  likely: z.number().finite().min(0),
  high: z.number().finite().min(0),
  source: SizingSource,
  confidence: z.enum(CONFIDENCES),
  /** A premissa por trás do número (por que este valor, por que esta faixa). */
  rationale: txt(1000).min(5, 'Explique a premissa (ao menos 5 caracteres).'),
  enabled: z.boolean().default(true),
  origin: z.enum(['SISTEMA', 'EQUIPE']).default('EQUIPE'),
  official: OfficialRef.nullable().optional(),
}).strict().superRefine((a, ctx) => {
  const role = ASSUMPTION_ROLES[a.role];
  const issue = (path: string, message: string) => ctx.addIssue({ code: 'custom', path: [path], message });
  if (!(a.low <= a.likely && a.likely <= a.high)) issue('likely', 'Use conservador ≤ provável ≤ superior.');
  if (role.kind === 'RATE' && a.high > 100) issue('high', 'Taxas vão de 0% a 100%.');
  if (role.kind === 'COUNT' && !a.unit) issue('unit', 'Informe a unidade da contagem (empresas, estabelecimentos ou pessoas).');
  if (role.kind === 'MONEY' && (!a.currency || !a.per)) issue('currency', 'Valor monetário exige moeda e periodicidade (ano ou mês).');
  if (role.kind === 'FX' && (!a.fromCurrency || !a.currency || a.fromCurrency === a.currency)) issue('fromCurrency', 'Câmbio exige moeda de origem e de destino diferentes.');
  if (role.kind === 'FX' && a.low <= 0) issue('low', 'Câmbio precisa ser maior que zero.');
  if (a.role === 'SAM_FILTER' && !a.dimension) issue('dimension', 'Diga qual aspecto o filtro representa (região, porte, aderência…).');
  if (a.source.kind === 'OFICIAL' && !a.source.url && !a.source.reference) issue('source', 'Dado oficial precisa de link ou da tabela de origem.');
  if ((a.source.kind === 'OFICIAL' || a.source.kind === 'PUBLICA') && !a.source.referenceDate) issue('source', 'Informe a data de referência do dado (ano ou mês).');
});
export type SizingAssumption = z.infer<typeof SizingAssumption>;

export const SizingPeriod = z.object({
  label: txt(60).min(2),
  start: z.string().trim().regex(/^\d{4}-\d{2}$/).nullable().optional(),
  end: z.string().trim().regex(/^\d{4}-\d{2}$/).nullable().optional(),
}).strict().refine((p) => !p.start || !p.end || p.start <= p.end, 'O fim do período vem depois do início.');
export type SizingPeriod = z.infer<typeof SizingPeriod>;

export interface SizingInputs {
  method: SizingMethod;
  currency: SizingCurrency;
  period: SizingPeriod;
  assumptions: SizingAssumption[];
}

// ── Resultado ───────────────────────────────────────────────────────────────────────────────────────────────────────
export interface SizingRange { low: number; likely: number; high: number }
export interface SizingOperand { id: string | null; label: string; value: SizingRange; unit: string; sourceKind: SizingSourceKind | null; sourceName: string | null; note?: string }
export interface SizingStep {
  level: SizingLevel; measure: 'EMPRESAS' | 'VALOR'; path: 'TOP_DOWN' | 'BOTTOM_UP' | 'COMBINADO';
  label: string; formula: string; operands: SizingOperand[]; result: SizingRange; unit: string;
}
export interface SizingLevelResult {
  level: SizingLevel;
  status: 'ESTIMADO' | 'DADOS_INSUFICIENTES';
  companies: SizingRange | null;
  value: SizingRange | null;
  /** Por que a receita foi (ou não foi) estimada. */
  valueNote: string;
  confidence: SizingConfidence | null;
  missing: string[];
  /** Ids das premissas que sustentam o número. */
  basedOn: string[];
}
export interface SizingPathResult { method: 'TOP_DOWN' | 'BOTTOM_UP'; levels: Record<SizingLevel, SizingLevelResult>; steps: SizingStep[]; issues: string[] }
export interface SizingReduction { id: string; label: string; level: 'SAM' | 'SOM'; dimension: FilterDimension | null; fraction: number; removedPct: number; from: number; to: number; measure: 'EMPRESAS' | 'VALOR' }
export interface SizingCheck { kind: 'ALERTA' | 'INFO'; text: string }
export interface SizingResult {
  engine: typeof SIZING_ENGINE_VERSION;
  method: SizingMethod; currency: SizingCurrency; period: SizingPeriod;
  levels: Record<SizingLevel, SizingLevelResult>;
  paths: SizingPathResult[];
  steps: SizingStep[];
  reductions: SizingReduction[];
  gaps: { tamToSam: { companies: number | null; value: number | null; pct: number | null }; samToSom: { companies: number | null; value: number | null; pct: number | null } };
  rates: Array<{ id: string; label: string; value: SizingRange; kind: 'TAXA' | 'CAMBIO' }>;
  checks: SizingCheck[];
  confidence: SizingConfidence | null;
  sufficient: boolean;
  sourcesByKind: Partial<Record<SizingSourceKind, number>>;
}

// ── Aritmética de intervalos (todas as grandezas são ≥ 0, então o produto é monotônico) ─────────────────────────────
const R = (a: SizingAssumption): SizingRange => ({ low: a.low, likely: a.likely, high: a.high });
const pctR = (r: SizingRange): SizingRange => ({ low: r.low / 100, likely: r.likely / 100, high: r.high / 100 });
const mul = (a: SizingRange, b: SizingRange): SizingRange => ({ low: a.low * b.low, likely: a.likely * b.likely, high: a.high * b.high });
const scale = (a: SizingRange, k: number): SizingRange => ({ low: a.low * k, likely: a.likely * k, high: a.high * k });
const minR = (a: SizingRange, b: SizingRange): SizingRange => ({ low: Math.min(a.low, b.low), likely: Math.min(a.likely, b.likely), high: Math.min(a.high, b.high) });
const CONF_RANK: Record<SizingConfidence, number> = { ALTA: 2, MEDIA: 1, BAIXA: 0 };
const RANK_CONF: SizingConfidence[] = ['BAIXA', 'MEDIA', 'ALTA'];
export function minConfidence(xs: Array<SizingConfidence | null | undefined>): SizingConfidence | null {
  const v = xs.filter((x): x is SizingConfidence => !!x);
  return v.length ? RANK_CONF[Math.min(...v.map((x) => CONF_RANK[x]))]! : null;
}
const lower = (c: SizingConfidence): SizingConfidence => RANK_CONF[Math.max(0, CONF_RANK[c] - 1)]!;

/** Arredonda para `sig` algarismos significativos — o resultado nunca é mais preciso que as premissas permitem. */
export function roundSig(v: number, sig = 2): number {
  if (!Number.isFinite(v) || v === 0) return 0;
  const d = Math.ceil(Math.log10(Math.abs(v)));
  const p = sig - d;
  const m = 10 ** p;
  return Math.round(v * m) / m;
}
/** Contagens de empresas: inteiras e com 2 algarismos significativos acima de 100. */
export const roundCount = (v: number) => (v < 100 ? Math.round(v) : Math.round(roundSig(v, 2)));

const unitOfCount = (a: SizingAssumption) => (a.unit ?? 'EMPRESAS') as CountUnit;
const operand = (a: SizingAssumption, value: SizingRange, unit: string, note?: string): SizingOperand => ({ id: a.id, label: a.label, value, unit, sourceKind: a.source.kind, sourceName: a.source.name, ...(note ? { note } : {}) });

interface Ctx { inp: SizingInputs; active: SizingAssumption[]; one: (r: AssumptionRole) => SizingAssumption | null; filters: SizingAssumption[]; issues: string[] }

/** Converte uma premissa monetária para a moeda da análise e para valor ANUAL; devolve null (com o motivo) quando não dá. */
function moneyOf(ctx: Ctx, a: SizingAssumption, steps: SizingStep[], level: SizingLevel, path: SizingStep['path']): { value: SizingRange; ops: SizingOperand[]; used: string[] } | string {
  let v = R(a);
  const ops: SizingOperand[] = [operand(a, v, `${a.currency}/${a.per === 'MES' ? 'mês' : 'ano'}`)];
  const used = [a.id];
  if (a.per === 'MES') {
    v = scale(v, 12);
    ops.push({ id: null, label: 'Meses no ano (valor mensal → anual)', value: { low: 12, likely: 12, high: 12 }, unit: 'meses', sourceKind: null, sourceName: null });
  }
  if (a.currency !== ctx.inp.currency) {
    const fx = ctx.active.find((x) => x.role === 'FX_RATE' && x.fromCurrency === a.currency && x.currency === ctx.inp.currency);
    if (!fx) return `Falta a taxa de câmbio ${a.currency}→${ctx.inp.currency} para usar “${a.label}”.`;
    v = mul(v, R(fx));
    ops.push(operand(fx, R(fx), `${ctx.inp.currency} por ${a.currency}`));
    used.push(fx.id);
  }
  if (ops.length > 1) steps.push({ level, measure: 'VALOR', path, label: `${a.label} em ${ctx.inp.currency} por ano`, formula: ops.map((o) => o.label).join(' × '), operands: ops, result: v, unit: `${ctx.inp.currency}/ano` });
  return { value: v, ops: [{ id: a.id, label: a.label, value: v, unit: `${ctx.inp.currency}/ano`, sourceKind: a.source.kind, sourceName: a.source.name, ...(ops.length > 1 ? { note: 'convertido' } : {}) }], used };
}

const emptyLevel = (level: SizingLevel, missing: string[], valueNote = 'Sem estimativa de empresas, não há receita a estimar.'): SizingLevelResult =>
  ({ level, status: 'DADOS_INSUFICIENTES', companies: null, value: null, valueNote, confidence: null, missing, basedOn: [] });

function confidenceOf(ctx: Ctx, ids: string[]): SizingConfidence | null {
  const used = ctx.active.filter((a) => ids.includes(a.id));
  let c = minConfidence(used.map((a) => a.confidence));
  if (!c) return null;
  if (used.some((a) => a.source.kind === 'IA')) c = 'BAIXA';
  return c;
}

/** Um caminho (top-down ou bottom-up) completo: empresas e, quando sustentado, valor, nos três níveis. */
function computePath(ctx: Ctx, method: 'TOP_DOWN' | 'BOTTOM_UP'): SizingPathResult {
  const steps: SizingStep[] = [];
  const issues: string[] = [];
  const path = method;
  const cur = ctx.inp.currency;
  // ── TAM em empresas
  let tamC: SizingRange | null = null; let tamCIds: string[] = [];
  const uni = ctx.one('UNIVERSE');
  const missingTam: string[] = [];
  if (uni) {
    const u = unitOfCount(uni);
    if (u === 'EMPRESAS') { tamC = R(uni); tamCIds = [uni.id]; steps.push({ level: 'TAM', measure: 'EMPRESAS', path, label: 'TAM em empresas', formula: uni.label, operands: [operand(uni, R(uni), 'empresas')], result: tamC, unit: 'empresas' }); }
    else {
      const conv = ctx.one('BUYER_CONVERSION');
      if (!conv) { const m = `O universo está em ${COUNT_UNIT_LABEL[u]}: ${u === 'PESSOAS' ? 'população' : 'estabelecimentos'} não são empresas compradoras. Informe a conversão em empresas compradoras.`; missingTam.push(m); issues.push(m); }
      else {
        tamC = mul(R(uni), pctR(R(conv))); tamCIds = [uni.id, conv.id];
        steps.push({ level: 'TAM', measure: 'EMPRESAS', path, label: 'TAM em empresas', formula: `${uni.label} × ${conv.label}`, operands: [operand(uni, R(uni), COUNT_UNIT_LABEL[u]), operand(conv, pctR(R(conv)), 'fração')], result: tamC, unit: 'empresas' });
      }
    }
  }
  // ── TAM em valor
  const valueSteps: SizingStep[] = [];
  let tamV: SizingRange | null = null; let tamVIds: string[] = []; let tamVNote = '';
  const ticket = ctx.one('ANNUAL_VALUE');
  let ticketV: { value: SizingRange; ops: SizingOperand[]; used: string[] } | null = null;
  if (ticket) {
    const t = moneyOf(ctx, ticket, valueSteps, 'TAM', path);
    if (typeof t === 'string') issues.push(t); else ticketV = t;
  }
  if (method === 'TOP_DOWN') {
    const spend = ctx.one('MARKET_SPEND');
    const rev = ctx.one('SECTOR_REVENUE');
    const share = ctx.one('SPEND_SHARE');
    if (spend) {
      const m = moneyOf(ctx, spend, valueSteps, 'TAM', path);
      if (typeof m === 'string') issues.push(m);
      else { tamV = m.value; tamVIds = m.used; tamVNote = `Gasto do mercado com a categoria (${spend.source.name}).`; valueSteps.push({ level: 'TAM', measure: 'VALOR', path, label: 'TAM em valor', formula: spend.label, operands: m.ops, result: tamV, unit: `${cur}/ano` }); }
    } else if (rev) {
      if (!share) issues.push('Faturamento setorial não é mercado: informe a parcela do faturamento gasta com a categoria para usá-lo.');
      else {
        const m = moneyOf(ctx, rev, valueSteps, 'TAM', path);
        if (typeof m === 'string') issues.push(m);
        else {
          tamV = mul(m.value, pctR(R(share))); tamVIds = [...m.used, share.id]; tamVNote = `Faturamento do setor × parcela gasta com a categoria (${share.source.name}).`;
          valueSteps.push({ level: 'TAM', measure: 'VALOR', path, label: 'TAM em valor', formula: `${rev.label} × ${share.label}`, operands: [...m.ops, operand(share, pctR(R(share)), 'fração')], result: tamV, unit: `${cur}/ano` });
        }
      }
    }
  }
  if (!tamV && tamC && ticketV) {
    tamV = mul(tamC, ticketV.value); tamVIds = [...tamCIds, ...ticketV.used]; tamVNote = 'Empresas do TAM × valor por cliente.';
    valueSteps.push({ level: 'TAM', measure: 'VALOR', path, label: 'TAM em valor', formula: `TAM (empresas) × ${ticket!.label}`, operands: [{ id: null, label: 'TAM (empresas)', value: tamC, unit: 'empresas', sourceKind: null, sourceName: null }, ...ticketV.ops], result: tamV, unit: `${cur}/ano` });
  }
  if (!tamC && !tamV) {
    if (!missingTam.length) missingTam.push(method === 'TOP_DOWN' ? 'Universo de empresas, ou gasto do mercado com a categoria, ou faturamento do setor com a parcela gasta com a categoria.' : 'Universo de empresas (contagem de empresas nas atividades do escopo).');
  }
  // ── SAM (filtros multiplicados; bottom-up pode usar contagem direta)
  const filters = ctx.filters;
  const qualified = method === 'BOTTOM_UP' ? ctx.one('QUALIFIED_ACCOUNTS') : null;
  let samC: SizingRange | null = null; let samCIds: string[] = [];
  let samV: SizingRange | null = null; let samVIds: string[] = [];
  const missingSam: string[] = [];
  const filterFactor = filters.reduce<SizingRange>((acc, f) => mul(acc, pctR(R(f))), { low: 1, likely: 1, high: 1 });
  const filterOps = filters.map((f) => operand(f, pctR(R(f)), 'fração'));
  if (qualified && unitOfCount(qualified) !== 'EMPRESAS') { const m = `“${qualified.label}” não está em empresas: contas qualificadas precisam ser empresas.`; missingSam.push(m); issues.push(m); }
  else if (qualified) {
    samC = R(qualified); samCIds = [qualified.id];
    steps.push({ level: 'SAM', measure: 'EMPRESAS', path, label: 'SAM em empresas (contagem direta)', formula: qualified.label, operands: [operand(qualified, R(qualified), 'empresas')], result: samC, unit: 'empresas' });
    if (tamC && samC.likely > tamC.likely) issues.push('As contas qualificadas superam o TAM: revise o universo ou a contagem.');
  } else if (filters.length && tamC) {
    samC = mul(tamC, filterFactor); samCIds = [...tamCIds, ...filters.map((f) => f.id)];
    steps.push({ level: 'SAM', measure: 'EMPRESAS', path, label: 'SAM em empresas', formula: ['TAM (empresas)', ...filters.map((f) => f.label)].join(' × '), operands: [{ id: null, label: 'TAM (empresas)', value: tamC, unit: 'empresas', sourceKind: null, sourceName: null }, ...filterOps], result: samC, unit: 'empresas' });
  }
  if (!filters.length && !qualified && (tamC || tamV)) missingSam.push(method === 'BOTTOM_UP' ? 'Contas qualificadas contadas ou ao menos um filtro de compatibilidade (região, porte, aderência).' : 'Ao menos um filtro de compatibilidade (região, porte, aderência, restrição). Sem filtro, o SAM seria igual ao TAM.');
  if (!tamC && !tamV) missingSam.push('TAM.');
  if (tamV && filters.length && method === 'TOP_DOWN') {
    samV = mul(tamV, filterFactor); samVIds = [...tamVIds, ...filters.map((f) => f.id)];
    valueSteps.push({ level: 'SAM', measure: 'VALOR', path, label: 'SAM em valor', formula: ['TAM (valor)', ...filters.map((f) => f.label)].join(' × '), operands: [{ id: null, label: 'TAM (valor)', value: tamV, unit: `${cur}/ano`, sourceKind: null, sourceName: null }, ...filterOps], result: samV, unit: `${cur}/ano` });
  } else if (samC && ticketV) {
    samV = mul(samC, ticketV.value); samVIds = [...samCIds, ...ticketV.used];
    valueSteps.push({ level: 'SAM', measure: 'VALOR', path, label: 'SAM em valor', formula: `SAM (empresas) × ${ticket!.label}`, operands: [{ id: null, label: 'SAM (empresas)', value: samC, unit: 'empresas', sourceKind: null, sourceName: null }, ...ticketV.ops], result: samV, unit: `${cur}/ano` });
  }
  // ── SOM
  let somC: SizingRange | null = null; let somCIds: string[] = [];
  let somV: SizingRange | null = null; let somVIds: string[] = [];
  const missingSom: string[] = [];
  if (method === 'TOP_DOWN') {
    const share = ctx.one('SOM_SHARE');
    if (!share) missingSom.push('Parcela alcançável no período (SOM).');
    if (share && samC) {
      somC = mul(samC, pctR(R(share))); somCIds = [...samCIds, share.id];
      steps.push({ level: 'SOM', measure: 'EMPRESAS', path, label: 'SOM em empresas', formula: `SAM (empresas) × ${share.label}`, operands: [{ id: null, label: 'SAM (empresas)', value: samC, unit: 'empresas', sourceKind: null, sourceName: null }, operand(share, pctR(R(share)), 'fração')], result: somC, unit: 'empresas' });
    }
    if (share && samV) {
      somV = mul(samV, pctR(R(share))); somVIds = [...samVIds, share.id];
      valueSteps.push({ level: 'SOM', measure: 'VALOR', path, label: 'SOM em valor', formula: `SAM (valor) × ${share.label}`, operands: [{ id: null, label: 'SAM (valor)', value: samV, unit: `${cur}/ano`, sourceKind: null, sourceName: null }, operand(share, pctR(R(share)), 'fração')], result: somV, unit: `${cur}/ano` });
    }
  } else {
    const cap = ctx.one('SALES_CAPACITY');
    const win = ctx.one('WIN_RATE');
    if (!cap) missingSom.push('Contas trabalhadas no período (capacidade comercial).');
    if (!win) missingSom.push('Taxa de conversão.');
    if (cap && unitOfCount(cap) !== 'EMPRESAS') { const m = `“${cap.label}” precisa estar em empresas (contas).`; missingSom.push(m); issues.push(m); }
    else if (cap && win && samC) {
      const raw = mul(R(cap), pctR(R(win)));
      somC = minR(raw, samC); somCIds = [...samCIds, cap.id, win.id];
      const capped = raw.high > samC.high || raw.likely > samC.likely || raw.low > samC.low;
      steps.push({ level: 'SOM', measure: 'EMPRESAS', path, label: 'SOM em empresas', formula: `mínimo(${cap.label} × ${win.label}; SAM)`, operands: [operand(cap, R(cap), 'contas'), operand(win, pctR(R(win)), 'fração'), { id: null, label: 'SAM (empresas) — teto', value: samC, unit: 'empresas', sourceKind: null, sourceName: null }], result: somC, unit: 'empresas' });
      if (capped) issues.push('A capacidade comercial × conversão supera o SAM em algum cenário: o SOM foi limitado ao SAM.');
    }
  }
  if (method === 'BOTTOM_UP' && somC && ticketV) {
    somV = mul(somC, ticketV.value); somVIds = [...somCIds, ...ticketV.used];
    valueSteps.push({ level: 'SOM', measure: 'VALOR', path, label: 'SOM em valor', formula: `SOM (empresas) × ${ticket!.label}`, operands: [{ id: null, label: 'SOM (empresas)', value: somC, unit: 'empresas', sourceKind: null, sourceName: null }, ...ticketV.ops], result: somV, unit: `${cur}/ano` });
  }
  if (!samC && !samV) missingSom.push('SAM.');
  const noValue = method === 'TOP_DOWN'
    ? 'Receita não estimada: falta o valor por cliente (ticket) ou o gasto do mercado com a categoria.'
    : 'Receita não estimada: falta o valor por cliente (ticket).';
  const lvl = (level: SizingLevel, c: SizingRange | null, cIds: string[], v: SizingRange | null, vIds: string[], missing: string[], valueOk: string): SizingLevelResult => {
    if (!c && !v) return emptyLevel(level, missing.length ? missing : ['Premissas do nível anterior.'], c ? noValue : 'Sem estimativa neste nível, não há receita a estimar.');
    const ids = [...new Set([...cIds, ...vIds])];
    return { level, status: 'ESTIMADO', companies: c, value: v, valueNote: v ? valueOk : noValue, confidence: confidenceOf(ctx, ids), missing: [], basedOn: ids };
  };
  return {
    method,
    levels: {
      TAM: lvl('TAM', tamC, tamCIds, tamV, tamVIds, missingTam, tamVNote || 'Empresas × valor por cliente.'),
      SAM: lvl('SAM', samC, samCIds, samV, samVIds, missingSam, method === 'TOP_DOWN' && tamV && filters.length ? 'TAM em valor × filtros de compatibilidade.' : 'Empresas do SAM × valor por cliente.'),
      SOM: lvl('SOM', somC, somCIds, somV, somVIds, missingSom, method === 'TOP_DOWN' ? 'SAM em valor × parcela alcançável.' : 'Empresas do SOM × valor por cliente.'),
    },
    steps: [...steps, ...valueSteps.sort((a, b) => SIZING_LEVELS.indexOf(a.level) - SIZING_LEVELS.indexOf(b.level))],
    issues,
  };
}

/** Combina dois caminhos: a faixa cobre os dois (menor conservador, maior superior) e o provável é a média dos prováveis. */
function combine(td: SizingRange | null, bu: SizingRange | null): SizingRange | null {
  if (td && bu) return { low: Math.min(td.low, bu.low), likely: (td.likely + bu.likely) / 2, high: Math.max(td.high, bu.high) };
  return td ?? bu;
}

/** Calcula TAM, SAM e SOM a partir das premissas. Puro e determinístico: mesma entrada, mesmo resultado (API e tela). */
export function computeSizing(inp: SizingInputs): SizingResult {
  const active = inp.assumptions.filter((a) => a.enabled !== false);
  const issues: string[] = [];
  const one = (role: AssumptionRole) => {
    const xs = active.filter((a) => a.role === role);
    if (xs.length > 1) issues.push(`Há ${xs.length} premissas ativas de “${ASSUMPTION_ROLES[role].label}”: só a primeira entra na conta. Desative as outras.`);
    return xs[0] ?? null;
  };
  const ctx: Ctx = { inp, active, one, filters: active.filter((a) => a.role === 'SAM_FILTER'), issues };
  const paths: SizingPathResult[] = inp.method === 'COMBINED' ? [computePath(ctx, 'TOP_DOWN'), computePath(ctx, 'BOTTOM_UP')] : [computePath(ctx, inp.method)];
  const checks: SizingCheck[] = [];
  let levels: Record<SizingLevel, SizingLevelResult>;
  const steps: SizingStep[] = paths.flatMap((p) => p.steps);
  if (paths.length === 1) levels = paths[0]!.levels;
  else {
    const [td, bu] = paths as [SizingPathResult, SizingPathResult];
    levels = {} as Record<SizingLevel, SizingLevelResult>;
    for (const L of SIZING_LEVELS) {
      const a = td.levels[L], b = bu.levels[L];
      if (a.status !== 'ESTIMADO' && b.status !== 'ESTIMADO') { levels[L] = emptyLevel(L, [...new Set([...a.missing.map((m) => `Top-down: ${m}`), ...b.missing.map((m) => `Bottom-up: ${m}`)])]); continue; }
      const companies = combine(a.companies, b.companies), value = combine(a.value, b.value);
      const ids = [...new Set([...a.basedOn, ...b.basedOn])];
      let conf = minConfidence([a.status === 'ESTIMADO' ? a.confidence : null, b.status === 'ESTIMADO' ? b.confidence : null]);
      for (const [m, x, y] of [['empresas', a.companies, b.companies], ['valor', a.value, b.value]] as const) {
        if (x && y && Math.min(x.likely, y.likely) > 0) {
          const ratio = Math.max(x.likely, y.likely) / Math.min(x.likely, y.likely);
          if (ratio >= 2) { checks.push({ kind: 'ALERTA', text: `${L} em ${m}: top-down e bottom-up divergem ${ratio.toFixed(1).replace('.', ',')} vezes no cenário provável. Revise as premissas antes de usar o número.` }); if (conf) conf = lower(conf); }
        }
        if (x && y) steps.push({ level: L, measure: m === 'empresas' ? 'EMPRESAS' : 'VALOR', path: 'COMBINADO', label: `${L} em ${m} (combinado)`, formula: 'conservador = menor dos dois · provável = média dos dois · superior = maior dos dois',
          operands: [{ id: null, label: `${L} top-down`, value: x, unit: m, sourceKind: null, sourceName: null }, { id: null, label: `${L} bottom-up`, value: y, unit: m, sourceKind: null, sourceName: null }], result: (m === 'empresas' ? companies : value)!, unit: m === 'empresas' ? 'empresas' : `${inp.currency}/ano` });
      }
      const only = a.status !== 'ESTIMADO' ? 'bottom-up' : b.status !== 'ESTIMADO' ? 'top-down' : null;
      if (only) checks.push({ kind: 'INFO', text: `${L}: só o caminho ${only} tem premissas suficientes; a combinação usa apenas ele.` });
      levels[L] = { level: L, status: 'ESTIMADO', companies, value, valueNote: value ? 'Faixa que cobre os dois caminhos.' : (a.valueNote || b.valueNote), confidence: conf, missing: [], basedOn: ids };
    }
  }
  // Hierarquia: SAM ≤ TAM e SOM ≤ SAM (empresas) no cenário provável.
  const L = levels;
  if (L.TAM.companies && L.SAM.companies && L.SAM.companies.likely > L.TAM.companies.likely * 1.0001) checks.push({ kind: 'ALERTA', text: 'O SAM provável ficou maior que o TAM: revise o universo ou as contas qualificadas.' });
  // Piso observado: empresas reais já identificadas.
  const observed = active.find((a) => a.role === 'OBSERVED_ACCOUNTS');
  if (observed && L.SAM.companies) {
    if (L.SAM.companies.low < observed.likely) checks.push({ kind: 'ALERTA', text: `O SAM conservador (${roundCount(L.SAM.companies.low)}) é menor que as ${observed.likely} empresa(s) já identificadas: os filtros podem estar restritivos demais.` });
    else checks.push({ kind: 'INFO', text: `O SAM conservador cobre as ${observed.likely} empresa(s) já identificadas no InteliMove (piso observado).` });
  }
  // Faixas muito largas e fontes de IA.
  for (const lv of SIZING_LEVELS) {
    const c = L[lv].companies ?? L[lv].value;
    if (c && c.low > 0 && c.high / c.low >= 10) checks.push({ kind: 'ALERTA', text: `${lv}: a faixa vai de 1 a ${Math.round(c.high / c.low)} (superior ÷ conservador). Estreite as premissas mais incertas para um número útil.` });
  }
  if (active.some((a) => a.source.kind === 'IA')) checks.push({ kind: 'ALERTA', text: 'Há premissa com fonte “estimativa de IA”: não é dado oficial e a confiança dos níveis que a usam é baixa.' });
  const notOfficial = active.filter((a) => a.origin === 'SISTEMA' && a.official?.values && (a.low !== a.official.values.low || a.likely !== a.official.values.likely || a.high !== a.official.values.high));
  for (const a of notOfficial) checks.push({ kind: 'INFO', text: `“${a.label}” foi ajustada pela equipe: deixou de ser o dado oficial publicado.` });
  for (const p of paths) for (const i of p.issues) issues.push(paths.length > 1 ? `${SIZING_METHOD_LABEL[p.method]}: ${i}` : i);
  checks.unshift(...[...new Set(issues)].map((text) => ({ kind: 'ALERTA' as const, text })));
  // Fatores de redução (cenário provável, caminho que tem a cadeia): filtros do SAM e a passagem SAM→SOM.
  const reductions: SizingReduction[] = [];
  const main = paths.find((p) => p.levels.TAM.status === 'ESTIMADO' && p.levels.SAM.status === 'ESTIMADO') ?? paths[0]!;
  const tam = main.levels.TAM; const measure: 'EMPRESAS' | 'VALOR' = tam.companies ? 'EMPRESAS' : 'VALOR';
  const base = tam.companies?.likely ?? tam.value?.likely ?? null;
  if (base !== null && !(main.method === 'BOTTOM_UP' && ctx.one('QUALIFIED_ACCOUNTS'))) {
    let run = base;
    for (const f of ctx.filters) {
      const frac = f.likely / 100; const to = run * frac;
      reductions.push({ id: f.id, label: f.label, level: 'SAM', dimension: (f.dimension ?? null) as FilterDimension | null, fraction: frac, removedPct: Math.round((1 - frac) * 1000) / 10, from: run, to, measure });
      run = to;
    }
  }
  const sam = main.levels.SAM, som = main.levels.SOM;
  const samBase = (measure === 'EMPRESAS' ? sam.companies : sam.value)?.likely, somTo = (measure === 'EMPRESAS' ? som.companies : som.value)?.likely;
  if (samBase && somTo !== undefined && samBase > 0) {
    const lbl = main.method === 'TOP_DOWN' ? ctx.one('SOM_SHARE')?.label ?? 'Parcela alcançável' : 'Capacidade comercial × conversão';
    reductions.push({ id: main.method === 'TOP_DOWN' ? ctx.one('SOM_SHARE')?.id ?? 'som' : 'capacidade', label: lbl, level: 'SOM', dimension: null, fraction: somTo / samBase, removedPct: Math.round((1 - somTo / samBase) * 1000) / 10, from: samBase, to: somTo, measure });
  }
  reductions.sort((a, b) => b.removedPct - a.removedPct);
  const gap = (a: SizingLevelResult, b: SizingLevelResult) => ({
    companies: a.companies && b.companies ? a.companies.likely - b.companies.likely : null,
    value: a.value && b.value ? a.value.likely - b.value.likely : null,
    pct: a.companies && b.companies && a.companies.likely > 0 ? b.companies.likely / a.companies.likely : a.value && b.value && a.value.likely > 0 ? b.value.likely / a.value.likely : null,
  });
  const rates = active.filter((a) => ASSUMPTION_ROLES[a.role].kind === 'RATE' || a.role === 'FX_RATE')
    .map((a) => ({ id: a.id, label: a.label, value: a.role === 'FX_RATE' ? R(a) : pctR(R(a)), kind: (a.role === 'FX_RATE' ? 'CAMBIO' : 'TAXA') as 'TAXA' | 'CAMBIO' }));
  const sourcesByKind: Partial<Record<SizingSourceKind, number>> = {};
  for (const a of active) sourcesByKind[a.source.kind] = (sourcesByKind[a.source.kind] ?? 0) + 1;
  const estimated = SIZING_LEVELS.filter((l) => L[l].status === 'ESTIMADO');
  return {
    engine: SIZING_ENGINE_VERSION, method: inp.method, currency: inp.currency, period: inp.period,
    levels: L, paths, steps, reductions, gaps: { tamToSam: gap(L.TAM, L.SAM), samToSom: gap(L.SAM, L.SOM) }, rates, checks,
    confidence: minConfidence(estimated.map((l) => L[l].confidence)), sufficient: estimated.length === 3, sourcesByKind,
  };
}

/** Limitações sempre declaradas (mais as específicas do resultado). */
export function sizingLimitations(inp: SizingInputs, r: SizingResult): string[] {
  const out: string[] = [
    'Estimativa, não medição: os três cenários (conservador, provável e superior) vêm das faixas informadas em cada premissa.',
    'Os números exibidos são arredondados a 2 algarismos significativos; o valor bruto do cálculo aparece só na reprodução, e não indica precisão maior.',
  ];
  const off = inp.assumptions.filter((a) => a.enabled !== false && a.source.kind === 'OFICIAL');
  if (off.some((a) => /CEMPRE|Cadastro Central de Empresas/i.test(`${a.source.name} ${a.source.reference ?? ''}`))) {
    out.push('IBGE/CEMPRE conta empresas e outras organizações formais com CNPJ (inclui administração pública e entidades sem fins lucrativos; não inclui o MEI) pela atividade PRINCIPAL; empresas que compram, mas atuam em outra atividade principal, ficam fora.');
    out.push('A distribuição por porte do IBGE é de unidades locais (estabelecimentos), usada como aproximação da distribuição de empresas.');
  }
  if (inp.assumptions.filter((a) => a.enabled !== false && a.role === 'SAM_FILTER').length >= 2) out.push('Os filtros do SAM se multiplicam: a conta supõe que são independentes (ex.: a distribuição de porte é a mesma dentro da região escolhida). Quando não forem, ajuste a faixa do filtro.');
  if (off.length) out.push(`Dados oficiais têm defasagem: o ano de referência mais recente usado é ${off.map((a) => a.source.referenceDate ?? '').sort().at(-1) || 'não informado'}.`);
  if (!r.levels.SOM.value && !r.levels.SAM.value) out.push('Receita potencial não estimada: nenhuma premissa de valor (ticket ou gasto do mercado) sustenta um número em dinheiro.');
  if (inp.assumptions.some((a) => a.enabled !== false && a.role === 'OBSERVED_ACCOUNTS')) out.push('Empresas já identificadas no InteliMove são amostra observada (piso), não o tamanho do mercado.');
  if (inp.assumptions.some((a) => a.enabled !== false && a.source.kind === 'IA')) out.push('Premissas com fonte de IA não são dado oficial e precisam de validação antes de decisões.');
  return out;
}

// ── Formatação compartilhada (tela, CSV e exportação usam a mesma regra de arredondamento) ──────────────────────────
const CUR_SYM: Record<SizingCurrency, string> = { BRL: 'R$', USD: 'US$', EUR: '€' };
export function formatCompact(v: number): string {
  const a = Math.abs(v);
  const f = (x: number, u: string) => `${x.toLocaleString('pt-BR', { maximumFractionDigits: x < 10 ? 1 : 0 })}${u}`;
  if (a >= 1e9) return f(roundSig(v / 1e9, 2), ' bi');
  if (a >= 1e6) return f(roundSig(v / 1e6, 2), ' mi');
  if (a >= 1e4) return f(roundSig(v / 1e3, 2), ' mil');
  return roundSig(v, 2).toLocaleString('pt-BR', { maximumFractionDigits: 2 });
}
export const formatCount = (v: number) => (v < 100 ? Math.round(v).toLocaleString('pt-BR') : formatCompact(roundCount(v)));
export const formatMoney = (v: number, c: SizingCurrency) => `${CUR_SYM[c]} ${formatCompact(v)}`;
export const formatPct = (fraction: number) => `${(Math.round(fraction * 1000) / 10).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
/** Valor bruto do cálculo (reprodução): até 6 algarismos significativos, sem notação compacta. */
export const formatRaw = (v: number) => roundSig(v, 6).toLocaleString('pt-BR', { maximumFractionDigits: 6 });

// ── Pedidos ─────────────────────────────────────────────────────────────────────────────────────────────────────────
export const SizingScopeInput = z.object({
  offeringIds: z.array(z.string().uuid()).max(30).default([]),
  icpIds: z.array(z.string().uuid()).max(20).default([]),
  segmentationId: z.string().uuid().nullable().optional(),
  segmentIds: z.array(z.string().uuid()).max(20).default([]),
  region: z.object({
    areas: z.array(z.object({ uf: z.string().trim().max(2).nullable().optional(), city: txt(80).nullable().optional() }).strict()).max(12).optional(),
    text: txt(200).nullable().optional(),
  }).strict().optional(),
  /** Atividades econômicas do escopo (CNAE de 2 a 7 dígitos); vazio = as do ICP e dos segmentos escolhidos. */
  cnaeCodes: z.array(z.string().trim().regex(/^[0-9]{2,7}$/, 'CNAE com 2 a 7 dígitos')).max(20).default([]),
}).strict().refine((s) => s.offeringIds.length + s.icpIds.length + s.segmentIds.length + s.cnaeCodes.length > 0 || !!s.region,
  'Escolha ao menos uma oferta, ICP, segmento, atividade (CNAE) ou região.');
export type SizingScopeInput = z.infer<typeof SizingScopeInput>;

export const CreateSizingRequest = z.object({
  name: txt(120).min(3),
  method: z.enum(SIZING_METHODS),
  currency: z.enum(SIZING_CURRENCIES).default('BRL'),
  period: SizingPeriod,
  scope: SizingScopeInput,
  /** Buscar dados oficiais (IBGE) na criação; desligado, a análise nasce só com o que já existe no InteliMove. */
  fetchOfficial: z.boolean().default(true),
  /** IM-UX-002: premissas da equipe já na criação (reaproveitadas de outra estimativa ou informadas na análise recomendada). */
  assumptions: z.array(SizingAssumption).max(10).optional(),
}).strict();
export type CreateSizingRequest = z.infer<typeof CreateSizingRequest>;

export const UpdateSizingRequest = z.object({
  version: z.number().int().min(1),
  name: txt(120).min(3).optional(),
  method: z.enum(SIZING_METHODS).optional(),
  currency: z.enum(SIZING_CURRENCIES).optional(),
  period: SizingPeriod.optional(),
  assumptions: z.array(SizingAssumption).max(40).optional(),
  status: z.enum(['ACTIVE', 'ARCHIVED']).optional(),
  justification: txt(1000).optional(),
}).strict();
export type UpdateSizingRequest = z.infer<typeof UpdateSizingRequest>;

export const RefreshOfficialRequest = z.object({
  version: z.number().int().min(1),
  /** Faixas de pessoal ocupado (IBGE) do filtro de porte; vazio = sem filtro de porte oficial. */
  sizeBands: z.array(z.string().trim().max(20)).max(12).optional(),
  justification: txt(1000).optional(),
}).strict();
export type RefreshOfficialRequest = z.infer<typeof RefreshOfficialRequest>;

// ── Respostas ───────────────────────────────────────────────────────────────────────────────────────────────────────
export interface SizingScopeDto {
  offerings: Array<{ id: string; name: string }>;
  icps: Array<{ id: string; name: string }>;
  segmentation: { id: string; name: string; versionNo: number | null } | null;
  segments: Array<{ id: string; name: string; klass: string | null; score: number | null }>;
  regionLabel: string; regionInput: { areas?: Array<{ uf?: string | null; city?: string | null }>; text?: string | null };
  cnae: Array<{ code: string; label: string | null; origin: string }>;
  /** IM-EXT-018-C01: códigos selecionados × consultados × consolidados (sem dupla contagem). Ausente em versões antigas. */
  cnaeScope?: CnaeScopeNormalization;
}
export interface OfficialDatumDto {
  key: string; dataset: string; table: string; variable: string; unit: string; period: string; locality: string; classification: string;
  value: number; url: string; fetchedAt: string;
}
export interface OfficialSizeBandDto { id: string; label: string; value: number }
export interface SizingOfficialDto {
  status: 'OK' | 'PARCIAL' | 'INDISPONIVEL' | 'NAO_APLICAVEL' | 'NAO_BUSCADO';
  detail: string; fetchedAt: string | null; period: string | null;
  data: OfficialDatumDto[];
  /** Distribuição oficial por faixa de pessoal ocupado (unidades locais de entidades empresariais), para escolher o porte. */
  sizeBands: { locality: string; classification: string; unit: string; total: number; bands: OfficialSizeBandDto[]; selected: string[]; url: string } | null;
  notes: string[];
}
export interface SizingReferenceDto { text: string; origin: string; method: string | null; hypothesis: boolean; date: string | null; url: string | null }
export interface SizingVersionDto {
  id: string; versionNo: number; trigger: 'CREATED' | 'ASSUMPTIONS' | 'CONFIG' | 'OFFICIAL_DATA' | 'RECALCULATED';
  note: string | null; justification: string | null; createdAt: string; createdByName: string | null;
  method: SizingMethod; currency: SizingCurrency;
  summary: Record<SizingLevel, { status: string; companies: number | null; value: number | null }>;
}
export interface SizingDetailDto {
  id: string; name: string; status: 'ACTIVE' | 'ARCHIVED'; version: number; createdAt: string; updatedAt: string;
  method: SizingMethod; currency: SizingCurrency; period: SizingPeriod;
  scope: SizingScopeDto;
  assumptions: SizingAssumption[];
  result: SizingResult;
  limitations: string[];
  official: SizingOfficialDto;
  references: SizingReferenceDto[];
  viewing: SizingVersionDto; isCurrent: boolean; versions: SizingVersionDto[];
  editable: boolean; warnings: string[];
}
export interface SizingListItemDto {
  id: string; name: string; status: 'ACTIVE' | 'ARCHIVED'; method: SizingMethod; currency: SizingCurrency; periodLabel: string; regionLabel: string;
  versionNo: number; updatedAt: string; segmentationId: string | null; segmentIds: string[]; segmentNames: string[];
  summary: SizingVersionDto['summary']; confidence: SizingConfidence | null;
}
export interface SizingCompareDto {
  from: SizingVersionDto; to: SizingVersionDto;
  assumptionChanges: Array<{ id: string; label: string; change: 'INCLUIDA' | 'REMOVIDA' | 'ALTERADA'; before: string | null; after: string | null }>;
  configChanges: string[];
  levelChanges: Array<{ level: SizingLevel; before: { companies: number | null; value: number | null; status: string }; after: { companies: number | null; value: number | null; status: string } }>;
}
export interface SizingHistoryEntryDto { id: string; action: string; at: string; actor: string | null; justification: string | null; versionNo: number | null; before: Record<string, unknown>; after: Record<string, unknown> }
export interface SizingOptionsDto {
  offerings: Array<{ id: string; name: string }>;
  icps: Array<{ id: string; name: string; cnaeCodes: string[]; locations: string[] }>;
  segmentations: Array<{ id: string; name: string; versionNo: number; segments: Array<{ id: string; name: string; klass: string; score: number | null; cnaeCodes: string[] }> }>;
  regionSuggestions: string[];
  contextConfirmed: boolean;
  officialAvailable: boolean;
}

/** Texto curto de uma premissa (comparação de versões, histórico e CSV). */
export function describeAssumption(a: SizingAssumption): string {
  const kind = ASSUMPTION_ROLES[a.role].kind;
  const f = (v: number) => (kind === 'RATE' ? `${v.toLocaleString('pt-BR')}%` : kind === 'MONEY' ? `${a.currency} ${v.toLocaleString('pt-BR')}${a.per === 'MES' ? '/mês' : '/ano'}` : kind === 'FX' ? `${v.toLocaleString('pt-BR')} ${a.currency}/${a.fromCurrency}` : `${v.toLocaleString('pt-BR')} ${COUNT_UNIT_LABEL[(a.unit ?? 'EMPRESAS') as CountUnit]}`);
  return `${f(a.low)} · ${f(a.likely)} · ${f(a.high)} — ${SOURCE_KIND_LABEL[a.source.kind]}: ${a.source.name}${a.enabled === false ? ' (desativada)' : ''}`;
}

// ── CNAE do escopo sem dupla contagem (IM-EXT-018-C01) ───────────────────────────────────────────────────────────────
export type CnaeLevel = 'DIVISAO' | 'GRUPO' | 'CLASSE' | 'SUBCLASSE';
export const CNAE_LEVEL_LABEL: Record<CnaeLevel, string> = { DIVISAO: 'divisão', GRUPO: 'grupo', CLASSE: 'classe', SUBCLASSE: 'subclasse' };
const cnaeLevel = (code: string): CnaeLevel => (code.length <= 2 ? 'DIVISAO' : code.length === 3 ? 'GRUPO' : code.length <= 5 ? 'CLASSE' : 'SUBCLASSE');
export interface CnaeScopeNormalization {
  /** Fonte oficial: até qual nível ela publica (CEMPRE 9418: classe). */
  sourceLevel: CnaeLevel;
  /** Códigos como escolhidos (sem repetição), com todas as origens (escopo, ICP, segmento…). */
  selected: Array<{ code: string; level: CnaeLevel; origins: string[] }>;
  /** Chaves efetivamente consultadas na fonte, cada uma uma vez só. */
  consulted: Array<{ key: string; level: CnaeLevel; covers: string[] }>;
  /** O que foi consolidado e por quê. */
  consolidated: Array<{ code: string; into: string; reason: 'DUPLICADO' | 'COBERTO_POR_NIVEL_MAIS_AMPLO' | 'NIVEL_DA_FONTE'; detail: string }>;
}
/**
 * Normaliza os CNAE do escopo para a fonte oficial SEM dupla contagem: o mesmo código vindo de várias origens conta uma
 * vez; subclasse vira a classe (nível que a fonte publica) — duas subclasses da mesma classe viram uma consulta só, e a
 * classe inclui as demais subclasses (isso é dito); código coberto por outro mais amplo (classe dentro do grupo, grupo
 * dentro da divisão) sai, porque o mais amplo já o contém. Mesma função na tela (prévia) e na API (consulta).
 */
export function normalizeCnaeScope(entries: Array<{ code: string; origin: string }>, sourceLevel: 'CLASSE' | 'GRUPO' = 'CLASSE'): CnaeScopeNormalization {
  const max = sourceLevel === 'CLASSE' ? 4 : 3;
  const byCode = new Map<string, string[]>();
  for (const e of entries) {
    const code = e.code.replace(/\D/g, '');
    if (code.length < 2) continue;
    const o = byCode.get(code) ?? [];
    if (!o.includes(e.origin)) o.push(e.origin);
    byCode.set(code, o);
  }
  const selected = [...byCode.entries()].map(([code, origins]) => ({ code, level: cnaeLevel(code), origins })).sort((a, b) => a.code.localeCompare(b.code));
  const consolidated: CnaeScopeNormalization['consolidated'] = [];
  for (const x of selected) if (x.origins.length > 1) consolidated.push({ code: x.code, into: x.code, reason: 'DUPLICADO', detail: `${x.code} veio de ${x.origins.join(' e ')}: consultado uma vez.` });
  // Nível que a fonte publica.
  const toKey = new Map<string, string[]>();
  for (const x of selected) {
    const key = x.code.slice(0, max);
    const list = toKey.get(key) ?? [];
    list.push(x.code);
    toKey.set(key, list);
    if (key !== x.code) consolidated.push({ code: x.code, into: key, reason: 'NIVEL_DA_FONTE', detail: `${x.code} (${CNAE_LEVEL_LABEL[x.level]}) consultado como ${CNAE_LEVEL_LABEL[cnaeLevel(key)]} ${key}: a fonte só publica até ${CNAE_LEVEL_LABEL[sourceLevel]}, e ${key} inclui também as demais ${x.level === 'SUBCLASSE' ? 'subclasses' : 'atividades'} dele.` });
  }
  // Coberto por um código mais amplo (prefixo) também consultado.
  const keys = [...toKey.keys()];
  const consulted: CnaeScopeNormalization['consulted'] = [];
  for (const k of keys.sort()) {
    const wider = keys.filter((o) => o !== k && k.startsWith(o)).sort((a, b) => a.length - b.length)[0];
    if (wider) {
      for (const c of toKey.get(k)!) consolidated.push({ code: c, into: wider, reason: 'COBERTO_POR_NIVEL_MAIS_AMPLO', detail: `${c} está dentro de ${CNAE_LEVEL_LABEL[cnaeLevel(wider)]} ${wider}, também selecionada: contado uma vez, dentro de ${wider}.` });
      continue;
    }
    consulted.push({ key: k, level: cnaeLevel(k), covers: [] });
  }
  for (const q of consulted) q.covers = selected.map((x) => x.code).filter((c) => c.startsWith(q.key)).sort();
  return { sourceLevel, selected, consulted, consolidated };
}
